/**
 * Контракт HTTP-фетчера мониторинга. docs/phase-2/01-monitoring.md §4.1.
 *
 * Правило R8: этот файл достижим из входа `.`, поэтому здесь нет Response, Headers,
 * ReadableStream, Buffer и любых node:*-типов. Тело — Uint8Array, заголовки —
 * Readonly<Record<string, string>> с ключами в нижнем регистре.
 *
 * Реализация одна — NodeHttpFetcher в packages/source-parsers (§5); прокси и headless-браузер
 * остаются точками расширения за этим интерфейсом и в Б2 не пишутся.
 */

import * as z from 'zod';
import { utf8Len } from '../units.ts';

/** Потолок bodyExcerpt у http_error: байты UTF-8 после декодирования с заменой. */
export const HTTP_ERROR_BODY_EXCERPT_MAX_BYTES = 512;

export interface FetchRequest {
  readonly url: string;
  /** Только GET. POST наружу мониторингу не нужен, а его отсутствие — часть поверхности атаки. */
  readonly method: 'GET';
  readonly headers: Readonly<Record<string, string>>;
  /** Потолок на всю операцию, включая чтение тела. */
  readonly timeoutMs: number;
  /** Жёсткий потолок прочитанных байт. Считается по потоку, а не по Content-Length (§16.2). */
  readonly maxBytes: number;
  /**
   * 'manual' — единственное допустимое значение, и оно в контракте, а не в реализации.
   * Следование редиректу стирает признак подмены: t.me/s/<канал> для несуществующего канала
   * отвечает 302 на t.me/<канал> и после follow приходит 200 с правдоподобной страницей (§6.3.2).
   */
  readonly redirect: 'manual';
}

export interface FetchedResponse {
  /** Запрошенный адрес. Редиректов здесь не бывает: их видно отдельным исходом. */
  readonly url: string;
  readonly status: number; // 200..299
  readonly headers: Readonly<Record<string, string>>; // ключи lowercase
  readonly body: Uint8Array;
  /** Заявленная кодировка из Content-Type, как пришла, без канонизации. null — не заявлена. */
  readonly declaredCharset: string | null;
  readonly elapsedMs: number;
  readonly bytes: number;
}

/**
 * У каждого отказа есть ИМЯ. Булевой `ok` в контракте нет намеренно: она склеивает
 * «сервер отдал 404» и «оборвалось соединение», а это разные решения в §7.3.
 */
export type FetchOutcome =
  | { readonly kind: 'response'; readonly response: FetchedResponse }
  | { readonly kind: 'redirect'; readonly status: number; readonly location: string | null }
  | {
      readonly kind: 'http_error';
      readonly status: number;
      readonly retryAfterMs: number | null;
      /**
       * ≤512 байт тела, декодированных как UTF-8 с заменой. Едет в source_fetch.error_text
       * и в письмо алерта: именно текст тела говорит «Cloudflare», «rate limit» или
       * «ваш IP в чёрном списке» — без него два 403 у разных источников неотличимы.
       */
      readonly bodyExcerpt: string;
    }
  | { readonly kind: 'too_large'; readonly readBytes: number; readonly maxBytes: number }
  | { readonly kind: 'timeout'; readonly elapsedMs: number; readonly phase: 'headers' | 'body' }
  | { readonly kind: 'network_error'; readonly code: string; readonly message: string };

export interface HttpFetcher {
  fetch(req: FetchRequest): Promise<FetchOutcome>;
}

/* ── zod-схемы: та же форма для валидации фикстур корпуса и сериализованных исходов ── */

/** Ключи — только в нижнем регистре: канонизацию делает фетчер, а не каждый потребитель. */
const LowercaseHeadersSchema = z
  .record(z.string(), z.string())
  .refine((h) => Object.keys(h).every((k) => k === k.toLowerCase()), {
    error: 'ключи заголовков обязаны быть в нижнем регистре',
  });

export const FetchRequestSchema = z.strictObject({
  url: z.string().min(1),
  method: z.literal('GET'),
  headers: LowercaseHeadersSchema,
  timeoutMs: z.int().positive(),
  maxBytes: z.int().positive(),
  redirect: z.literal('manual'),
});

export const FetchedResponseSchema = z.strictObject({
  url: z.string().min(1),
  status: z.int().min(200).max(299),
  headers: LowercaseHeadersSchema,
  body: z.instanceof(Uint8Array),
  declaredCharset: z.string().nullable(),
  elapsedMs: z.int().nonnegative(),
  bytes: z.int().nonnegative(),
});

export const FetchOutcomeSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('response'), response: FetchedResponseSchema }),
  z.strictObject({
    kind: z.literal('redirect'),
    status: z.int().min(100).max(599),
    location: z.string().nullable(),
  }),
  z.strictObject({
    kind: z.literal('http_error'),
    status: z.int().min(100).max(599),
    retryAfterMs: z.int().nonnegative().nullable(),
    bodyExcerpt: z.string().refine((s) => utf8Len(s) <= HTTP_ERROR_BODY_EXCERPT_MAX_BYTES, {
      error: `bodyExcerpt длиннее ${String(HTTP_ERROR_BODY_EXCERPT_MAX_BYTES)} байт UTF-8`,
    }),
  }),
  z.strictObject({
    kind: z.literal('too_large'),
    readBytes: z.int().nonnegative(),
    maxBytes: z.int().positive(),
  }),
  z.strictObject({
    kind: z.literal('timeout'),
    elapsedMs: z.int().nonnegative(),
    phase: z.enum(['headers', 'body']),
  }),
  z.strictObject({
    kind: z.literal('network_error'),
    code: z.string().min(1),
    message: z.string(),
  }),
]);
